import type { EChartsOption } from 'echarts'
import { axisTextStyle, formatCompactNumber, formatSignedShare, isFiniteNumber, tooltipBase, tooltipHtml } from './format'
import { divergingColor, labelColorOn, sequentialColor, type ChartTheme } from './palette'

export type HeatmapScale = 'sequential' | 'diverging'

export interface HeatmapAxisItem {
  key: string
  label: string
}

export interface HeatmapInput {
  rows: HeatmapAxisItem[]
  columns: HeatmapAxisItem[]
  /** Значения ячеек `[строка][колонка]`, показываются подписью и в подсказке; `null` — нет данных. */
  values: Array<Array<number | null>>
  /**
   * Чем красить ячейку, если не самим значением: например, Δ% к предыдущей неделе для «Трендов».
   * Для расходящейся шкалы это обычно доли изменения (0,25 = +25 %).
   */
  colorValues?: Array<Array<number | null>>
  scale?: HeatmapScale
  /** Предел расходящейся шкалы (по модулю); значения дальше обрезаются по краю. По умолчанию 0,5 (±50 %). */
  divergingLimit?: number
  valueFormat?: (value: number) => string
  /** Подпись цветового значения в подсказке (для Δ — «+12,0% к прошлой неделе»). */
  colorFormat?: (value: number) => string
  colorLabel?: string
  /** Подписи значений в ячейках (если влезают). */
  cellLabels?: boolean
  /** Ширина колонки подписей строк, px. */
  rowLabelWidth?: number
  /**
   * Формат значения ячейки по строке и колонке — когда у строк разные единицы (РНП: ₽, шт и % в одной матрице).
   * Используется и в подписи ячейки, и в подсказке; без него — `valueFormat` (подсказка) и короткая запись (подпись).
   */
  cellFormat?: (value: number, rowIndex: number, columnIndex: number) => string
  /** Показывать цветовое значение строкой подсказки (по умолчанию — да, если задано `colorValues`). */
  showColorInTooltip?: boolean
  /** Дополнительные строки подсказки ячейки (база сравнения, отклонение). */
  tooltipExtra?: (rowIndex: number, columnIndex: number) => Array<{ label: string; value: string }>
  /**
   * Видимые границы ячеек (1 px цвета оси) и заливка пустых ячеек цветом дорожки — для расходящейся шкалы, где
   * «норма» почти сливается с фоном карточки и без сетки матрица не читается (РНП).
   */
  cellBorders?: boolean
}

/** Подписи колонок: при больше чем 14 колонках — через одну, иначе даты налезают друг на друга. */
export const HEATMAP_LABEL_EVERY_OTHER = 14

export function heatmapColumnLabelInterval(columns: number): number {
  return columns > HEATMAP_LABEL_EVERY_OTHER ? 1 : 0
}

/** Высота под число строк: 28 px на строку + шапка с подписями колонок. */
export function heatmapHeight(rows: number): number {
  return Math.max(120, rows * 28 + 40)
}

/** Пределы последовательной шкалы по всем числам матрицы. */
export function valueExtent(matrix: Array<Array<number | null>>): { min: number; max: number } | null {
  let min = Infinity
  let max = -Infinity
  for (const row of matrix) {
    for (const value of row) {
      if (!isFiniteNumber(value)) continue
      if (value < min) min = value
      if (value > max) max = value
    }
  }
  return Number.isFinite(min) ? { min, max } : null
}

/** Цвет ячейки на выбранной шкале; `null` — ячейка без заливки. */
export function heatmapCellColor(
  value: number | null | undefined,
  scale: HeatmapScale,
  theme: ChartTheme,
  extent: { min: number; max: number } | null,
  divergingLimit: number,
): string | null {
  if (!isFiniteNumber(value)) return null
  if (scale === 'diverging') return divergingColor(theme, value, divergingLimit)
  if (!extent) return null
  return sequentialColor(theme, value, Math.min(0, extent.min), extent.max)
}

/**
 * `visualMap`, который ничего не перекрашивает. ECharts в режиме разработки отказывается рисовать heatmap без
 * `visualMap` («Heatmap must use with visualMap») — матрица выходит пустой. Цвет ячеек мы считаем сами (заливка
 * каждой точки), поэтому карта задаёт только непрозрачность 1 и внутри, и вне диапазона: ни одна ячейка не
 * перекрашивается и не прячется, даже если цветовое значение далеко за пределом шкалы.
 *
 * `dimension` — измерение точки с цветовым значением (у матрицы — 3, у календаря — 1).
 */
export function passThroughVisualMap(dimension: number, min: number, max: number, seriesIndex?: number | number[]) {
  return {
    type: 'continuous' as const,
    show: false,
    dimension,
    min,
    max,
    ...(seriesIndex !== undefined ? { seriesIndex } : {}),
    inRange: { opacity: 1 },
    outOfRange: { opacity: 1 },
  }
}

/** Измерения точки матрицы: колонка, строка, значение (подпись), цветовое значение. */
export const HEATMAP_COLOR_DIMENSION = 3

/**
 * Матрица «строки × колонки». Цвет считается здесь же (а не `visualMap`): так подпись в ячейке получает
 * контрастный цвет к своей заливке, а шкала — легенду в HTML под графиком (`HeatmapLegend`). Между ячейками —
 * зазор 2 px цвета поверхности. Строки и колонки — категории с ключами, подписи — через `formatter`, чтобы
 * одинаковые названия не склеивались в одну строку.
 */
export function buildHeatmapOption(input: HeatmapInput, theme: ChartTheme): EChartsOption {
  const scale = input.scale ?? 'sequential'
  const limit = input.divergingLimit ?? 0.5
  const colorMatrix = input.colorValues ?? input.values
  const extent = scale === 'sequential' ? valueExtent(colorMatrix) : null
  const valueFormat = input.valueFormat ?? ((value: number) => formatCompactNumber(value))
  const colorFormat = input.colorFormat ?? ((value: number) => (scale === 'diverging' ? formatSignedShare(value) : valueFormat(value)))
  const showLabels = input.cellLabels ?? input.columns.length <= 16

  const rowKeys = input.rows.map((_row, index) => `r${index}`)
  const columnKeys = input.columns.map((_column, index) => `c${index}`)

  const data: any[] = []
  input.rows.forEach((_row, rowIndex) => {
    input.columns.forEach((_column, columnIndex) => {
      const value = input.values[rowIndex]?.[columnIndex] ?? null
      const hasValue = isFiniteNumber(value)
      const colorValue = colorMatrix[rowIndex]?.[columnIndex] ?? null
      const fill = heatmapCellColor(colorValue, scale, theme, extent, limit)
      // Ячейка со значением, но без цвета (нет базы сравнения) — нейтральная дорожка, а не дыра в матрице.
      const cellFill = fill ?? (hasValue || input.cellBorders ? theme.track : 'transparent')
      // ECharts пропускает точку, у которой измерение 2 не число: пустую ячейку рисуем только в сетке (cellBorders),
      // там она нужна серой клеткой; без сетки пустая ячейка, как и раньше, не рисуется.
      const drawn: number | '-' = hasValue ? value : input.cellBorders ? 0 : '-'
      data.push({
        value: [columnIndex, rowIndex, drawn, isFiniteNumber(colorValue) ? colorValue : '-'],
        itemStyle: input.cellBorders
          ? { color: cellFill, borderColor: theme.axis, borderWidth: 1, borderRadius: 2 }
          : { color: cellFill, borderColor: theme.surface, borderWidth: 2, borderRadius: 3 },
        label: {
          show: showLabels && hasValue,
          color: fill ? labelColorOn(fill, theme) : theme.textMuted,
        },
      })
    })
  })

  return {
    backgroundColor: 'transparent',
    animation: false,
    textStyle: { fontFamily: theme.fontFamily },
    aria: { enabled: true },
    grid: { top: 28, left: 8, right: 8, bottom: 4, outerBoundsMode: 'same', outerBoundsContain: 'axisLabel' } as any,
    tooltip: {
      ...tooltipBase(theme),
      trigger: 'item',
      formatter: (params: any) => {
        const [columnIndex, rowIndex] = params.value ?? []
        const row = input.rows[rowIndex]
        const column = input.columns[columnIndex]
        if (!row || !column) return ''
        const value = input.values[rowIndex]?.[columnIndex] ?? null
        const colorValue = input.colorValues ? (input.colorValues[rowIndex]?.[columnIndex] ?? null) : null
        const showColor = Boolean(input.colorValues) && input.showColorInTooltip !== false
        const formatted = isFiniteNumber(value) ? (input.cellFormat ? input.cellFormat(value, rowIndex, columnIndex) : valueFormat(value)) : '—'
        return tooltipHtml(theme, `${row.label} · ${column.label}`, [
          { label: 'значение', value: formatted },
          ...(showColor ? [{ label: input.colorLabel ?? 'изменение', value: isFiniteNumber(colorValue) ? colorFormat(colorValue) : '—' }] : []),
          ...(input.tooltipExtra ? input.tooltipExtra(rowIndex, columnIndex) : []),
        ])
      },
    },
    visualMap: passThroughVisualMap(
      HEATMAP_COLOR_DIMENSION,
      scale === 'diverging' ? -limit : Math.min(0, extent?.min ?? 0),
      scale === 'diverging' ? limit : Math.max(extent?.max ?? 1, Math.min(0, extent?.min ?? 0) + 1),
    ),
    xAxis: {
      type: 'category',
      position: 'top',
      data: columnKeys,
      axisLine: { show: false },
      axisTick: { show: false },
      splitArea: { show: false },
      axisLabel: {
        ...axisTextStyle(theme),
        interval: heatmapColumnLabelInterval(input.columns.length),
        hideOverlap: true,
        formatter: (key: string) => input.columns[Number(key.slice(1))]?.label ?? '',
      },
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: rowKeys,
      triggerEvent: true,
      axisLine: { show: false },
      axisTick: { show: false },
      splitArea: { show: false },
      axisLabel: {
        ...axisTextStyle(theme),
        color: theme.textSecondary,
        fontSize: 12,
        width: input.rowLabelWidth ?? 180,
        overflow: 'truncate',
        interval: 0,
        formatter: (key: string) => input.rows[Number(key.slice(1))]?.label ?? '',
      },
    },
    series: [
      {
        type: 'heatmap',
        data,
        cursor: 'pointer',
        label: {
          show: showLabels,
          fontSize: 11,
          fontFamily: theme.fontFamily,
          formatter: (params: any) => {
            const [columnIndex, rowIndex] = params.value ?? []
            // Значение — из входа, а не из точки: у пустой клетки сетки в точке стоит 0 только для отрисовки.
            const raw = input.values[rowIndex]?.[columnIndex]
            if (!isFiniteNumber(raw)) return ''
            return input.cellFormat ? input.cellFormat(raw, rowIndex, columnIndex) : formatCompactNumber(raw)
          },
        },
        emphasis: { itemStyle: { borderColor: theme.text, borderWidth: 1 } },
      },
    ],
  } as EChartsOption
}
